import { useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useWorkspaceStore } from '@/stores/workspace.store';
import { authService } from '@/services/auth.service';
import { useToast } from '@/hooks/use-toast';

export function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { fetchWorkspaces, setActiveWorkspace } = useWorkspaceStore();
  const accepting = useRef(false);

  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }

    // Synchronous check: if there's no access token in storage, user is not logged in.
    // Don't wait for async auth initialization — redirect immediately.
    const hasToken = !!localStorage.getItem('access_token');

    if (!hasToken) {
      // Flow A/B: Not logged in → redirect to signup with invitation token
      navigate(`/signup?invitation=${encodeURIComponent(token)}`, { replace: true });
      return;
    }

    // Flow C: User has a token (logged in) → accept invitation directly
    if (accepting.current) return;
    accepting.current = true;

    authService.acceptInvitation(token)
      .then(async (res) => {
        const data = res.data.data;
        toast({
          title: 'Invitation accepted!',
          description: `You've joined ${data?.workspace_name ?? 'the workspace'}.`,
        });

        await fetchWorkspaces();
        const newWs = useWorkspaceStore.getState().workspaces.find(
          (w) => w.id === data?.workspace_id
        );
        if (newWs) setActiveWorkspace(newWs);

        navigate('/dashboard', { replace: true });
      })
      .catch((err) => {
        toast({
          title: 'Error',
          description: err?.response?.data?.error?.message || 'Invalid or expired invitation.',
          variant: 'destructive',
        });
        navigate('/dashboard', { replace: true });
      });
  }, [token]);

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
        <p className="text-sm text-muted-foreground">Processing invitation...</p>
      </div>
    </div>
  );
}
